import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
// import {
//   useGetAiSettingsQuery,
//   useSaveAiSettingsMutation,
// } from '../store/api/endpoints/ai-settings';
import {
  useGetAiSettingsQuery,
  useGetUserAiSettingsQuery,
  useSaveAiSettingsMutation,
} from 'api/collaborateApi';
// import {
//   AiSettingsFormData,
//   AiTeamMateProps,
//   CreditsInfo,
// } from '../store/types/ai-settings';
import { useSelector } from 'react-redux';
import {
  AiSettingsFormData,
  AiTeamMateProps,
  CreditsInfo,
} from '../../../types/api/collaborate';
import {
  getSelectedSiteCollaborateInfo,
  getSelectedSiteId,
} from '../store/siteInfoSlice';

export const AI_BOTS: AiTeamMateProps[] = [
  {
    id: 'pixel',
    name: 'Pixel - UI & Design Consistency',
    function: 'UI & Design Consistency',
    description:
      "Nothing escapes my trained eye - I'll keep your designs crisp and consistent!",
    avatar:
      'https://atarim-wpfeedback-image.s3.eu-west-1.amazonaws.com/images/ai-bots/pixel.png',
    backgroundColor: 'bg-tertiary',
    enabled: false,
  },
  {
    id: 'navi',
    name: 'Navi - UX & Accessibility',
    function: 'UX & Accessibility',
    description:
      'Lost users? Not on my watch - I make every experience seamless and accessible!',
    avatar:
      'https://atarim-wpfeedback-image.s3.eu-west-1.amazonaws.com/images/ai-bots/navi.png',
    backgroundColor: 'bg-ata-green',
    enabled: false,
  },
  {
    id: 'index',
    name: 'Index - SEO & Performance',
    function: 'SEO & Performance',
    description:
      'I fine-tune your site for search engines - because visibility is everything!',
    avatar:
      'https://atarim-wpfeedback-image.s3.eu-west-1.amazonaws.com/images/ai-bots/index.png',
    backgroundColor: 'bg-ata-light-blue',
    enabled: false,
  },
  {
    id: 'lexi',
    name: 'Lexi - Content & Readability',
    function: 'Content & Readability',
    description:
      "Words matter - I'll make sure your content is polished, clear, and compelling!",
    avatar:
      'https://atarim-wpfeedback-image.s3.eu-west-1.amazonaws.com/images/ai-bots/lexi.png',
    backgroundColor: 'bg-ata-orange',
    enabled: false,
  },
  {
    id: 'glitch',
    name: 'Glitch - Functionality & Broken Links',
    function: 'Functionality & Broken Links',
    description:
      "Glitches beware - I'll find and flag them before they cause trouble!",
    avatar:
      'https://atarim-wpfeedback-image.s3.eu-west-1.amazonaws.com/images/ai-bots/glitch.png',
    backgroundColor: 'bg-ata-dark-orange',
    enabled: false,
  },
];

export const PROJECT_TYPE_OPTIONS = [
  { label: 'Marketing Website', value: 'marketing-website' },
  { label: 'eCommerce', value: 'ecommerce' },
  { label: 'SaaS Platform', value: 'saas-platform' },
  { label: 'Portfolio', value: 'portfolio' },
  { label: 'Corporate Website', value: 'corporate-website' },
  { label: 'Landing Page', value: 'landing-page' },
  { label: 'Web App', value: 'web-app' },
  { label: 'Internal Tool', value: 'internal-tool' },
  { label: 'Membership Site', value: 'membership-site' },
  { label: 'Blog or News Site', value: 'blog-news-site' },
  { label: 'Non-Profit Website', value: 'non-profit-website' },
  { label: 'Government Website', value: 'government-website' },
  { label: 'Educational Platform', value: 'educational-platform' },
  { label: 'Event or Conference Site', value: 'event-conference-site' },
  { label: 'Community Forum', value: 'community-forum' },
  { label: 'Client Portal', value: 'client-portal' },
  { label: 'Product Documentation Site', value: 'product-documentation-site' },
  { label: 'Multilingual Website', value: 'multilingual-website' },
  { label: 'Custom Web Solution', value: 'custom-web-solution' },
];

export const useAiSettings = (globalSettings = false) => {
  // API hooks
  const collaborateInfo = useSelector(getSelectedSiteCollaborateInfo);
  const siteId = useSelector(getSelectedSiteId);
  const workspaceId = collaborateInfo?.result?.workspace_id || 0;
  const { data: userAiSettings } = useGetUserAiSettingsQuery(
    String(workspaceId),
    {
      skip: !workspaceId || !siteId,
    }
  );

  const {
    data: aiSettings,
    isLoading,
    error,
    refetch,
  } = useGetAiSettingsQuery(
    {
      workspaceId: String(workspaceId),
      siteId: siteId || '',
    },
    { skip: !workspaceId || !siteId }
  );
  const [saveAiSettings, { isLoading: isSaving }] = useSaveAiSettingsMutation();

  // Form state
  const [formData, setFormData] = useState<AiSettingsFormData>({
    brandName: '',
    projectTypes: [],
    targetAudience: '',
    voiceTone: '',
    customGuidelines: '',
    enabledBots: [],
    reviewIntent: '',
  });
  const [reviewIntent, setReviewIntent] = useState('');

  const [hasChanges, setHasChanges] = useState(false);

  // Initialize form data when AI settings are loaded
  useEffect(() => {
    if (!globalSettings && aiSettings) {
      const newFormData = {
        brandName: aiSettings.settings?.brand_name || '',
        projectTypes: aiSettings.settings?.project_types || [],
        targetAudience: aiSettings.settings?.audience || '',
        voiceTone: aiSettings.settings?.voice_tone || '',
        customGuidelines: aiSettings.settings?.guidelines || '',
        enabledBots:
          aiSettings.settings?.enabled_experts?.length > 0
            ? aiSettings.settings?.enabled_experts
            : AI_BOTS.filter((bot) => bot.enabled).map((bot) => bot.id),
      };
      setFormData((prev) => ({
        ...newFormData,
        reviewIntent: prev.reviewIntent,
      }));
      setHasChanges(false);
    } else if (globalSettings && userAiSettings) {
      const newFormData = {
        brandName: userAiSettings.settings?.brand_name || '',
        projectTypes: userAiSettings.settings?.project_types || [],
        targetAudience: userAiSettings.settings?.audience || '',
        voiceTone: userAiSettings.settings?.voice_tone || '',
        customGuidelines: userAiSettings.settings?.guidelines || '',
        enabledBots: userAiSettings.settings?.enabled_experts || [],
      };
      setFormData((prev) => ({
        ...newFormData,
        reviewIntent: prev.reviewIntent,
      }));
      setHasChanges(false);
    }
  }, [aiSettings, userAiSettings, globalSettings]);

  useEffect(() => {
    setFormData((prev) => {
      if (prev.reviewIntent === reviewIntent) return prev;
      return { ...prev, reviewIntent };
    });
  }, [reviewIntent]);

  // Credits information
  const creditsInfo: CreditsInfo = useMemo(() => {
    if (!aiSettings) return { used: 0, total: 100, percentage: 0 };

    const used = aiSettings.settings?.credits_used || 0;
    const total = aiSettings.settings?.credits_total || 100;
    // const total = aiSettings.credits_total || 100;
    const percentage = total > 0 ? (used / total) * 100 : 0;

    return { used, total, percentage };
  }, [aiSettings]);

  // AI Bots with current enabled state
  const botsWithState = useMemo(() => {
    return AI_BOTS.map((bot) => ({
      ...bot,
      enabled: formData.enabledBots.includes(bot.id),
    }));
  }, [formData.enabledBots]);

  // Form handlers
  const updateFormField = <K extends keyof AiSettingsFormData>(
    field: K,
    value: AiSettingsFormData[K]
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleBotToggle = (botId: string, enabled: boolean) => {
    const newEnabledBots = enabled
      ? [...formData.enabledBots, botId]
      : formData.enabledBots.filter((id) => id !== botId);

    updateFormField('enabledBots', newEnabledBots);
  };

  const resetForm = () => {
    if (aiSettings) {
      setFormData({
        brandName: aiSettings.settings?.brand_name || '',
        projectTypes: aiSettings.settings?.project_types || [],
        targetAudience: aiSettings.settings?.audience || '',
        voiceTone: aiSettings.settings?.voice_tone || '',
        customGuidelines: aiSettings.settings?.guidelines || '',
        enabledBots: aiSettings.settings?.enabled_experts || [],
        reviewIntent: reviewIntent,
      });
      setHasChanges(false);
    }
  };

  const saveSettings = async () => {
    try {
      const requestData = {
        brand_name: formData.brandName,
        project_types: formData.projectTypes,
        audience: formData.targetAudience,
        voice_tone: formData.voiceTone,
        guidelines: formData.customGuidelines,
        enabled_experts: formData.enabledBots,
      };

      // Create new settings
      const saveData = requestData;
      await saveAiSettings({
        data: saveData,
        workspace_id: String(workspaceId),
        site_id: siteId || '',
      }).unwrap();
      toast.success('AI settings saved successfully!');

      setHasChanges(false);
    } catch (error) {
      console.error('Failed to save AI settings:', error);
      toast.error('Failed to save AI settings. Please try again.');
    }
  };

  return {
    // Data
    aiSettings,
    formData,
    botsWithState,
    creditsInfo,
    reviewIntent,

    // State
    isLoading,
    isSaving,
    hasChanges,
    error,

    // Actions
    updateFormField,
    handleBotToggle,
    saveSettings,
    resetForm,
    refetch,
    setReviewIntent,

    // Computed
    isProcessing: isSaving,
    canSave: hasChanges && !isSaving,
  };
};
